import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { CategoryEntries } from '../components/gradebook/CategoryEntries';
import { GpaHeadline, SubjectGrades } from '../components/gradebook/GradeSummary';
import { authHeaders } from '../components/gradebook/gradeUtils';
import { ParentEmailsEditor } from '../components/gradebook/ParentEmailsEditor';
import { WorksheetReview } from '../components/gradebook/WorksheetReview';

const selCls = 'bg-black border-2 border-zinc-800 focus:border-orange-500 outline-none px-3 py-2 text-sm text-white';
const errMsg = (e) => e?.response?.data?.detail || 'Something went wrong';

export default function Gradebook() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState('');
  const [subject, setSubject] = useState('math');
  const [unit, setUnit] = useState(1);
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/academy/gradebook/config').then((r) => setConfig(r.data));
    api.get('/academy/admin/students-lite', { headers: authHeaders() })
      .then((r) => setStudents(r.data.students || []))
      .catch(() => toast.error('Teacher/admin access required'));
  }, []);

  const load = useCallback(async () => {
    if (!sid) return;
    setLoading(true);
    try { setBook((await api.get(`/academy/admin/gradebook/${sid}`, { headers: authHeaders() })).data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  const add = async (category, f) => {
    try {
      await api.post(`/academy/admin/gradebook/${sid}/entries`, { subject, category, unit, label: f.label || null, score: Number(f.score), max_score: Number(f.max_score) }, { headers: authHeaders() });
      toast.success('Score saved'); await load(); return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const save = async (entry, f) => {
    try {
      await api.put(`/academy/admin/gradebook/entries/${entry.id}`, { subject: entry.subject, category: entry.category, unit: entry.unit, label: f.label || null, score: Number(f.score), max_score: Number(f.max_score), date: entry.date }, { headers: authHeaders() });
      toast.success('Score updated'); await load(); return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const remove = async (entry) => {
    if (!window.confirm('Delete this score?')) return;
    try { await api.delete(`/academy/admin/gradebook/entries/${entry.id}`, { headers: authHeaders() }); toast.success('Score deleted'); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const unitEntries = (book?.entries || []).filter((e) => e.subject === subject && e.unit === unit);
  const maxUnit = Math.max(6, ...(book?.summary?.units || [1]));

  return (
    <div className="min-h-screen bg-black text-white" data-testid="gradebook-page">
      <div className="border-b-2 border-orange-500/30 px-4 py-4 max-w-7xl mx-auto flex items-center justify-between">
        <button onClick={() => navigate('/academy/teacher')} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gray-400 hover:text-white" data-testid="gradebook-back-btn"><ArrowLeft className="w-3.5 h-3.5" /> Teacher Portal</button>
        <h1 className="text-xl font-black uppercase tracking-tight">Gradebook</h1>
        <span className="w-24" />
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-3 mb-6">
          <select className={`${selCls} w-full sm:w-auto max-w-full`} value={sid} onChange={(e) => setSid(e.target.value)} data-testid="gradebook-student-select">
            <option value="">Select a student…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name} {s.grade_level !== undefined ? `· Gr ${s.grade_level}` : ''} · {s.email}</option>)}
          </select>
          <select className={selCls} value={unit} onChange={(e) => setUnit(Number(e.target.value))} data-testid="gradebook-unit-select">
            {Array.from({ length: maxUnit + 1 }, (_, i) => i + 1).map((u) => <option key={u} value={u}>Unit {u}</option>)}
          </select>
          <div className="flex border-2 border-zinc-800" data-testid="gradebook-subject-tabs">
            {(config?.subjects || []).map((s) => (
              <button key={s.key} onClick={() => setSubject(s.key)} className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors ${subject === s.key ? 'bg-orange-500 text-black' : 'text-zinc-400 hover:text-white'}`} data-testid={`gradebook-subject-${s.key}`}>{s.label}</button>
            ))}
          </div>
        </div>
        {sid && <ParentEmailsEditor key={sid} studentId={sid} />}
        {sid && <WorksheetReview key={`wr-${sid}`} studentId={sid} />}
        {!sid ? (
          <div className="text-zinc-500 text-sm py-20 text-center" data-testid="gradebook-empty">Pick a student to enter scores.</div>
        ) : loading && !book ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
        ) : book && (
          <div className="grid lg:grid-cols-[1fr_380px] gap-6">
            <div className="space-y-3">
              {config.categories.map((c) => (
                <CategoryEntries key={`${sid}-${subject}-${unit}-${c.key}`} category={c} entries={unitEntries.filter((e) => e.category === c.key)} onAdd={add} onSave={save} onDelete={remove} />
              ))}
            </div>
            <div className="space-y-4 lg:sticky lg:top-4 self-start" data-testid="gradebook-summary">
              <div className="text-[10px] font-black uppercase tracking-[0.3em] text-orange-400">{book.student.name} · Cumulative</div>
              <GpaHeadline summary={book.summary} />
              <SubjectGrades summary={book.summary} active={subject} onSelect={(k) => k !== 'all' && setSubject(k)} />
              <p className="text-[10px] text-zinc-500 leading-relaxed">Weights: IXL 20 · Exams 20 · Presentations 30 · Reading 10 · Elective 10 · Notebook 10. Empty categories are left out and remaining weights re-balanced. GPA = average of subject grade points (A+ 4.25 … D- 0.75).</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
